import { useState, useCallback, useRef, useEffect } from 'react';
import type { AttemptState, AttemptConfig, AttemptAnswer } from '../engine/types';
import { AttemptManager } from '../engine/attempt';

export interface UseAttemptOptions {
  contentId: string;
  maxAttempts?: number;
  allowRetry?: boolean;
  onAttempt?: (attempt: AttemptAnswer) => void;
  onComplete?: (state: AttemptState) => void;
}

export interface UseAttemptReturn {
  // State
  state: AttemptState;
  attempts: number;
  maxAttempts: number;
  isCompleted: boolean;
  isCorrect: boolean | null;
  
  // Computed
  canAttempt: boolean;
  canRetry: boolean;
  remainingAttempts: number;
  latestAnswer: AttemptAnswer | null;
  bestAnswer: AttemptAnswer | null;
  allAnswers: AttemptAnswer[];
  timeSpent: number;
  
  // Actions
  submit: (answer: unknown, isCorrect: boolean, score?: number, max?: number, feedback?: string) => AttemptAnswer;
  retry: () => void;
  reset: () => void;
  start: () => void;
  
  // Manager access
  manager: AttemptManager;
}

/**
 * Hook for managing content attempts
 */
export function useAttempt(options: UseAttemptOptions): UseAttemptReturn {
  const { contentId, maxAttempts = 0, allowRetry = true, onAttempt, onComplete } = options;

  // Create manager ref
  const managerRef = useRef<AttemptManager>();
  if (!managerRef.current) {
    const config: AttemptConfig = {
      contentId,
      maxAttempts,
      allowRetry,
      onAttempt,
      onComplete,
    };
    managerRef.current = new AttemptManager(config);
  }
  const manager = managerRef.current;

  // State
  const [state, setState] = useState<AttemptState>(() => manager.getState());

  // Subscribe to manager events
  useEffect(() => {
    const unsubscribe = manager.onAny(() => {
      setState(manager.getState());
    });

    return () => {
      unsubscribe();
    };
  }, [manager]);

  // Actions
  const submit = useCallback((
    answer: unknown,
    isCorrect: boolean,
    score: number = isCorrect ? 1 : 0,
    max: number = 1,
    feedback?: string
  ): AttemptAnswer => {
    return manager.submit(answer, isCorrect, score, max, feedback);
  }, [manager]);

  const retry = useCallback(() => {
    manager.retry();
  }, [manager]);

  const reset = useCallback(() => {
    manager.reset();
    setState(manager.getState());
  }, [manager]);

  const start = useCallback(() => {
    manager.start();
  }, [manager]);

  // Cleanup
  useEffect(() => {
    return () => {
      manager.destroy();
    };
  }, [manager]);

  return {
    // State
    state,
    attempts: state.attempts,
    maxAttempts: state.maxAttempts,
    isCompleted: state.isCompleted,
    isCorrect: state.isCorrect,
    
    // Computed
    canAttempt: manager.canAttempt(),
    canRetry: manager.canRetry(),
    remainingAttempts: manager.getRemainingAttempts(),
    latestAnswer: manager.getLatestAnswer(),
    bestAnswer: manager.getBestAnswer(),
    allAnswers: manager.getAllAnswers(),
    timeSpent: manager.getTimeSpent(),
    
    // Actions
    submit,
    retry,
    reset,
    start,
    
    // Manager access
    manager,
  };
}
